import React, { useCallback, useState } from "react";
import { Box, FormControlLabel, Radio, RadioGroup, Slider, Typography } from "@mui/material";

interface SubareaConfigPanelProps {
  subareaMode: "raw" | "combine";
  combineTargetSize: number;
  onChange: (config: { mode: "raw" } | { mode: "combine"; value: number }) => void;
  disabled?: boolean;
}

const SubareaConfigPanel: React.FC<SubareaConfigPanelProps> = ({ subareaMode, combineTargetSize, onChange, disabled = false }) => {
  const [localValue, setLocalValue] = useState(combineTargetSize);

  const handleModeChange = useCallback(
    (mode: "raw" | "combine") => {
      onChange(mode === "raw" ? { mode: "raw" } : { mode: "combine", value: combineTargetSize });
    },
    [onChange, combineTargetSize]
  );

  const handleTargetSizeChangeCommitted = useCallback((value: number) => onChange({ mode: "combine", value }), [onChange]);

  return (
    <Box sx={{ mb: 2, opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      <Typography fontWeight="500" sx={{ mb: 2 }}>
        Subarea configuration
      </Typography>
      <Box sx={{ px: 1 }}>
        <RadioGroup value={subareaMode} onChange={(e) => handleModeChange(e.target.value as "raw" | "combine")}>
          <FormControlLabel value="raw" control={<Radio size="small" />} label="Use census block groups as-is" />
          <FormControlLabel value="combine" control={<Radio size="small" />} label="Combine into larger subareas" />
        </RadioGroup>
        {subareaMode === "combine" && (
          <Box sx={{ px: 2, mt: 2 }}>
            <Slider
              value={localValue}
              size="small"
              onChange={(_, value) => setLocalValue(value as number)}
              onChangeCommitted={(_, value) => handleTargetSizeChangeCommitted(value as number)}
              min={500}
              max={9000}
              step={500}
              marks
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => value.toLocaleString()}
            />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          {subareaMode === "raw" ? (
            <>A subarea will be created for each census block group within your work area.</>
          ) : (
            <>
              Census block groups will be merged to create subareas having approximately <b>{localValue.toLocaleString()}</b> premises each.
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default SubareaConfigPanel;
